/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react"
import { DocumentsInbox } from "../DocumentsInbox"
import { FaceRecognition } from "../FaceRecognition"
import { Login } from "../Login"
import { PersonalDocument } from "../PersonalDocument"
import { AdditionalDocument } from "../AdditionalDocument"
import { DeviceScan } from "../DeviceScan"
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { NotFoundPage } from '../NotFoundPage'
import { ExpiredPage } from '../ExpiredPage'
import { LoadingPage } from '../LoadingPage'
import { CanceledPage } from '../CanceledPage'
import { SignedPage } from "../SignedPage"
import { OTPPage } from "../OTPPage"
import ErrorBoundaryComponent from "../ErrorBoundary"
import { ResumeOperation } from "../ResumeOperation"
import { PortabilityAndRefinancingSummary } from "../PortabilityAndRefinancingSummary"
import { RefinancingSummary } from "../RefinancingSummary"
import { TermsOfUse } from "../TermsOfUse"
import { FraudWarning } from "../FraudWarning"
import { ResumeInsurance } from "../ResumeInsurance"
import { TermOfAuthorization } from "../TermOfAuthorization"
import EnrichPersonalData from "../EnrichPersonalData"
import { setDatadogPageAction, setGlobalContext } from '../../services/datadog'
import { DeactivatedPage } from "../DeactivatedPage"
import { useThemeContext } from "../../contexts/ThemeContext"
import { GenericTerm } from "../GenericTerm"
import { ConfiaPage } from "../ConfiaPage"
import { CreditOperationAndSocialSecurityCardsTheme, PrivatePayrollAuctionTheme, SocialSecurityBenefitCardTheme } from "../customTheme"
import { AwaitingEnvelopeSubmit } from "../AwaitingEnvelopeSubmit"

export const MainContainer = (props) => {

    const { setClientThemeSettings } = useThemeContext();
    const { setSignMetadata, signMetadata } = props;

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [expired, setExpired] = useState(false)
    const [canceled, setCanceled] = useState(false)
    const [lendingReproved, setLendingReproved] = useState(false)
    const { signerHash } = useParams()
    const [signerStep, setSignerStep] = useState("")
    const [signData, setSignData] = useState({})
    const [alreadySigned, setAlreadySigned] = useState(false)
    const [cachedDocumentNumber, setCachedDocumentNumber] = useState(undefined)
    const [cachedBirthdate, setCachedBirthdate] = useState(undefined)
    const [appTheme, setAppTheme] = useState(undefined);
    const [awaitingEnvelopeSubmit, setAwaitingEnvelopeSubmit] = useState(false)
    const [pendingNextStep, setPendingNextStep] = useState(undefined)
    const loginHashQueryParam = new URLSearchParams(window.location.search).get('login_hash')
    const initialFetchDoneRef = useRef(false)

    const clearAwaitingEnvelopeSubmit = React.useCallback(() => {
        setAwaitingEnvelopeSubmit(false)
        setPendingNextStep(undefined)
    }, [])

    useEffect(() => {
        const responseInterceptor = axios.interceptors.response.use(
            (response) => {
                const url = response?.config?.url || ''
                if (url.includes('/signer') && response?.data?.next_step_blocked === true) {
                    setPendingNextStep(response.data.next_step)
                    setAwaitingEnvelopeSubmit(true)
                }
                return response
            },
            (error) => {
                const url = error?.config?.url || ''
                const status = error?.response?.status
                const data = error?.response?.data
                if (
                    url.includes('/signer') &&
                    status === 422 &&
                    (data?.title === 'Step blocked' || data?.blocked_reason === 'awaiting_envelope_submit')
                ) {
                    setAwaitingEnvelopeSubmit(true)
                }
                return Promise.reject(error)
            }
        )
        return () => {
            axios.interceptors.response.eject(responseInterceptor)
        }
    }, [])


    useEffect(() => {
        setDatadogPageAction(`/s/${signerHash} [@step=${signerStep}]`, {
            signerHash,
            signerStep,
            signData
        })


        const company_name = signData.signer_company_name ?? '';
        if (company_name) {
            setGlobalContext({ companyName: company_name });
        }

    }, [signerHash, signerStep, signData])

    useEffect(() => {
        if (
            signerStep === "authentication_code" &&
            signData.skip_initial_notification &&
            signData.signer_token
        ) {
            if (signerHash) {
                const tokenKey = `otp_sent_at_${signerHash}`;
                const lastSentTimestamp = sessionStorage.getItem(tokenKey);
                const now = new Date();
                const fiveMinutes = 5 * 60 * 1000;

                if (!lastSentTimestamp || (now - new Date(lastSentTimestamp)) > fiveMinutes) {
                    onSendOTPCode(signData.signer_token);
                    sessionStorage.setItem(tokenKey, now.toISOString());
                }
            }
        }
    }, [signerStep, signData, signerHash]);

    useEffect(() => {
        if (signData?.signature_settings?.app_theme) {
            setAppTheme(signData.signature_settings.app_theme)
        }
    }, [signData])

    const onSendOTPCode = (signer_token) => {
        const data = {
            "event_date": new Date().toISOString()
        }
        const config = {
            headers:
            {
                'Authorization': signer_token
            }
        }
        axios.post('/signer/authentication', data, config)
            .then((response) => {
            })
            .catch((error) => {
            })
    }

    const refreshSignData = async () => {
        try {

            const getResponse = await axios.get('/signer/hash/' + signerHash)
            setSignMetadata(getResponse.data?.signature_settings);

            const payload = {
                "document_number": cachedDocumentNumber
            }

            if (cachedBirthdate) {
                payload["birthdate"] = cachedBirthdate
            }

            const postResponse = await axios.post('/signer/hash/' + signerHash, payload)
            return { ...getResponse.data, ...postResponse.data }
        } catch {
            setError(true)
        }
    }

    const resetQueryParams = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete('document_number');
        url.searchParams.delete('birthdate');
        url.searchParams.delete('name');
        url.searchParams.delete('login_hash')
        window.history.replaceState({}, document.title, url.toString());
    }

    useEffect(() => {
        if (initialFetchDoneRef.current) return
        initialFetchDoneRef.current = true

        const defaultClientThemeSettings = {
            "logo_url": "https://qisign-app-production.s3.amazonaws.com/logos/qi_tech_azul_claro_escuro_principal.png",
            "font_color": "#000000",
            "button_color": "#4FCCED",
            "email_logo_url": "https://qisign-app-production.s3.amazonaws.com/logos/qi_tech_branco_email.png",
            "background_color": "#0E3573"
        }

        if (loginHashQueryParam) {
            const config = {
                headers:
                {
                    'Authorization': 'Bearer ' + loginHashQueryParam
                }
            }
            axios.get('/signer/hash/' + signerHash, config)
                .then((response) => {
                    setSignMetadata(response.data?.signature_settings);
                    setSignData((prevData) => ({ ...prevData, ...response.data }))
                    setClientThemeSettings(response.data.client_theme_settings ? { ...defaultClientThemeSettings, ...response.data.client_theme_settings } : defaultClientThemeSettings)
                    if (["signed", "sending_sign_receipt", "receipt_sent", "analyzing", "analyzed", "completed"].includes(response.data.status)) {
                        if (response.data.status !== "signed") {
                            setAlreadySigned(true)
                        }
                        setSignerStep("signed")
                    } else if (response.data.status === "canceled") {
                        setCanceled(true)
                    } else if (response.data.status === "expired") {
                        setExpired(true)
                    }
                    else {
                        setSignerStep("login")
                    }
                }).catch((error) => {
                    if (error.response?.status === 401) {
                        resetQueryParams()
                        axios.get('/signer/hash/' + signerHash)
                            .then((response) => {
                                setSignMetadata(response.data?.signature_settings);
                                setSignData((prevData) => ({ ...prevData, ...response.data }))
                                setClientThemeSettings(response.data.client_theme_settings ? { ...defaultClientThemeSettings, ...response.data.client_theme_settings } : defaultClientThemeSettings)
                                if (["signed", "sending_sign_receipt", "receipt_sent", "analyzing", "analyzed", "completed"].includes(response.data.status)) {
                                    if (response.data.status !== "signed") {
                                        setAlreadySigned(true)
                                    }
                                    setSignerStep("signed")
                                } else if (response.data.status === "canceled") {
                                    setCanceled(true)
                                } else if (response.data.status === "expired") {
                                    setExpired(true)
                                }
                                else {
                                    setSignerStep("login")
                                }
                            }).catch((error) => {
                                setError(true)
                            })
                            .finally(() => {
                                setLoading(false)
                            })
                    } else {
                        setError(true)
                    }
                })
                .finally(() => {
                    setLoading(false)
                })
        } else {
            axios.get('/signer/hash/' + signerHash)
                .then((response) => {
                    setSignMetadata(response.data?.signature_settings);
                    setSignData((prevData) => ({ ...prevData, ...response.data }))
                    setClientThemeSettings(response.data.client_theme_settings ? { ...defaultClientThemeSettings, ...response.data.client_theme_settings } : defaultClientThemeSettings)
                    if (["signed", "sending_sign_receipt", "receipt_sent", "analyzing", "analyzed", "completed"].includes(response.data.status)) {
                        if (response.data.status !== "signed") {
                            setAlreadySigned(true)
                        }
                        setSignerStep("signed")
                    } else if (response.data.status === "canceled") {
                        setCanceled(true)
                    } else if (response.data.status === "expired") {
                        setExpired(true)
                    }
                    else {
                        setSignerStep("login")
                    }
                }).catch((error) => {
                    setError(true)
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }, [signerHash, setClientThemeSettings, loginHashQueryParam])

    
    const getScenes = () => {
        if (loading) {
            if (signMetadata?.custom_theme === 'private_payroll_auction') {
                return <PrivatePayrollAuctionTheme.Loading />
            }
            return (
                <LoadingPage />
            )
        }

        if (error) {
            return (
                <NotFoundPage />
            )
        }

        if (awaitingEnvelopeSubmit && !canceled && !expired) {
            if (signData.signature_settings?.custom_theme === 'private_payroll_auction') {
                return (
                    <PrivatePayrollAuctionTheme.AwaitingEnvelopeSubmit
                        signerHash={signerHash}
                        setSignerStep={setSignerStep}
                        setSignData={setSignData}
                        clearAwaitingEnvelopeSubmit={clearAwaitingEnvelopeSubmit}
                        pendingNextStep={pendingNextStep}
                    />
                )
            }
            return (
                <AwaitingEnvelopeSubmit
                    signerHash={signerHash}
                    setSignerStep={setSignerStep}
                    setSignData={setSignData}
                    clearAwaitingEnvelopeSubmit={clearAwaitingEnvelopeSubmit}
                    pendingNextStep={pendingNextStep}
                />
            )
        }

        if (
            signData.signature_settings?.custom_theme === 'private_payroll_auction' &&
            (lendingReproved || (canceled && signData.lending_analysis?.status === 'reproved'))
        ) {
            return (
                <PrivatePayrollAuctionTheme.LendingReproved />
            )
        }

        if (canceled) {
            return (
                <CanceledPage />
            )
        }

        if (expired) {
            return (
                <ExpiredPage />
            )
        }

        if (signerStep === 'deactivated') {
            return (
                <DeactivatedPage />
            )
        }

        if (signerStep === "generic_term") {
            return (
                <GenericTerm
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                />
            )
        }

        if (signerStep === "enrich_personal_data") {
            if (signData.signature_settings?.custom_theme === 'private_payroll_auction') {
                return (
                    <PrivatePayrollAuctionTheme.EnrichPersonalData 
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                    />
                )
            }
            if (
                signData.signature_settings?.custom_theme === 'credit_operation_and_social_security_cards' &&
                signData.personal_data?.length === 1 &&
                signData.personal_data[0]?.type === 'address' &&
                signData.personal_data[0]?.validate === true
            ) {
                return (
                    <CreditOperationAndSocialSecurityCardsTheme.EnrichPersonalData
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                    />
                )
            }
            if (
                signData.signature_settings?.custom_theme === 'social_security_benefit_card' &&
                signData.personal_data?.length === 1 &&
                signData.personal_data[0]?.type === 'address' &&
                signData.personal_data[0]?.validate === true
            ) {
                return (
                    <SocialSecurityBenefitCardTheme.EnrichPersonalData
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                    />
                )
            }
            return (
                <EnrichPersonalData
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    refreshSignData={refreshSignData}
                    appTheme={appTheme}
                />
            )
        }

        if (signerStep === "lending_analysis") {
            if (signData.signature_settings?.custom_theme === 'private_payroll_auction') {
                return (
                    <PrivatePayrollAuctionTheme.LendingAnalysis
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                        setAlreadySigned={setAlreadySigned}
                        setCanceled={setCanceled}
                        setExpired={setExpired}
                        setLendingReproved={setLendingReproved}
                        signerHash={signerHash}
                    />
                )
            }
            return (
                <LoadingPage />
            )
        }

        if (signerStep === "login") {
            if (signData.signature_settings?.custom_theme === 'private_payroll_auction') {
                return (
                    <PrivatePayrollAuctionTheme.Login
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                        onSendOTPCode={onSendOTPCode}
                        setCachedDocumentNumber={setCachedDocumentNumber}
                        setCachedBirthdate={setCachedBirthdate}
                    />
                )
            }  
            if (signData.signature_settings?.custom_theme === 'credit_operation_and_social_security_cards') {
                return (
                    <CreditOperationAndSocialSecurityCardsTheme.Login
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                        onSendOTPCode={onSendOTPCode}
                        setCachedDocumentNumber={setCachedDocumentNumber}
                        setCachedBirthdate={setCachedBirthdate}
                    />
                )
            }
            if (signData.signature_settings?.custom_theme === 'social_security_benefit_card') {
                return (
                    <SocialSecurityBenefitCardTheme.Login
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                    />
                )
            }
            return (
                <Login
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    onSendOTPCode={onSendOTPCode}
                    setCachedDocumentNumber={setCachedDocumentNumber}
                    setCachedBirthdate={setCachedBirthdate}
                    appTheme={appTheme}
                />
            )
        }
        else if (signerStep === "terms_of_use") {
            return (
                <TermsOfUse
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    appTheme={appTheme}
                />
            )
        }
        else if (signerStep === "term_of_authorization") {
            return (
                <TermOfAuthorization
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    appTheme={appTheme}
                />
            )
        }
        else if (signerStep === "resume_operation") {
            if (signData.signature_settings?.custom_theme === 'private_payroll_auction') {
                return (
                    <PrivatePayrollAuctionTheme.ResumeOperation
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                    />
                )
            }
            if (signData.signature_settings?.custom_theme === 'credit_operation_and_social_security_cards') {
                return (
                    <CreditOperationAndSocialSecurityCardsTheme.ResumeOperation
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                    />
                )
            }
            return (
                <ResumeOperation
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    appTheme={appTheme}
                />
            )
        }
        else if (signerStep === "portability_and_refinancing_summary") {
            if (signData.signature_settings?.custom_theme === 'private_payroll_auction') {
                return (
                    <PrivatePayrollAuctionTheme.PortabilityAndRefinancingSummary
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                    />
                )
            }
            return (
                <PortabilityAndRefinancingSummary
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                />
            )
        }
        else if (signerStep === "refinancing_summary") {
            if (signData.signature_settings?.custom_theme === 'private_payroll_auction') {
                return (
                    <PrivatePayrollAuctionTheme.RefinancingSummary
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                    />
                )
            }
            return (
                <RefinancingSummary
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                />
            )
        }
        else if (signerStep === "resume_insurance") {
            return (
                <ResumeInsurance
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    appTheme={appTheme}
                />
            )
        }
        else if (signerStep === "fraud_warning") {
            return (
                <FraudWarning
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    appTheme={appTheme}
                />
            )
        }
        else if (signerStep === "document_display") {
            return (
                <DocumentsInbox
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    appTheme={appTheme}
                />
            )
        }
        else if (signerStep === "social_security_cards") {
            if (signData.signature_settings?.custom_theme === 'social_security_benefit_card') {
                return (
                    <SocialSecurityBenefitCardTheme.SocialSecurityCards
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                    />
                )
            }
            return (
                <CreditOperationAndSocialSecurityCardsTheme.SocialSecurityCards
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                />
            )
        }
        else if (signerStep === "face_recognition") {
            if (signData.provider === 'confia') {
                if (signData.signature_settings?.custom_theme === 'social_security_benefit_card') {
                    return (
                        <SocialSecurityBenefitCardTheme.ConfiaPage
                            setSignerStep={setSignerStep}
                            signData={signData}
                            setSignData={setSignData}
                            appTheme={appTheme}
                        />
                    )
                }
                if (signData.signature_settings?.custom_theme === 'credit_operation_and_social_security_cards') {
                    return (
                        <CreditOperationAndSocialSecurityCardsTheme.ConfiaPage
                            setSignerStep={setSignerStep}
                            signData={signData}
                            setSignData={setSignData}
                            appTheme={appTheme}
                        />
                    )
                }
                return (
                    <ConfiaPage
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                        appTheme={appTheme}
                    />
                )
            }

            if (signData.signature_settings?.custom_theme === 'private_payroll_auction') {
                return (
                    <PrivatePayrollAuctionTheme.FaceRecognition
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                        setAlreadySigned={setAlreadySigned}
                        setCanceled={setCanceled}
                        setExpired={setExpired}
                        appTheme={appTheme}
                        signerHash={signerHash}
                    />
                )
            }
            return (
                <FaceRecognition
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    setAlreadySigned={setAlreadySigned}
                    setCanceled={setCanceled}
                    setExpired={setExpired}
                    appTheme={appTheme}
                    signerHash={signerHash}
                />
            )
        }
        else if (signerStep === "personal_document") {
            if (signData.signature_settings?.custom_theme === 'credit_operation_and_social_security_cards') {
                return (
                    <CreditOperationAndSocialSecurityCardsTheme.PersonalDocument
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                        signerHash={signerHash}
                    />
                )
            }
            if (signData.signature_settings?.custom_theme === 'social_security_benefit_card') {
                return (
                    <SocialSecurityBenefitCardTheme.PersonalDocument
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                        signerHash={signerHash}
                    />
                )
            }
            if (signData.signature_settings?.custom_theme === 'private_payroll_auction') {
                return (
                    <PrivatePayrollAuctionTheme.PersonalDocument
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                        signerHash={signerHash}
                    />
                )
            }
            return (
                <PersonalDocument
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    appTheme={appTheme}
                    signerHash={signerHash}
                />
            )
        }
        else if (signerStep === "additional_document") {
            return (
                <AdditionalDocument
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    appTheme={appTheme}
                    signerHash={signerHash}
                />
            )
        }
        else if (signerStep === "device_scan") {
            if (signData?.signature_settings?.custom_theme === 'private_payroll_auction') {
                return (
                    <PrivatePayrollAuctionTheme.DeviceScan
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                        isBatch={false}
                        appTheme={appTheme}
                    />
                )
            }
            return (
                <DeviceScan
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    isBatch={false}
                    appTheme={appTheme}
                />
            )
        }
        else if (signerStep === "authentication_code") {
            if (signData?.signature_settings?.custom_theme === 'private_payroll_auction') {
                return (
                    <PrivatePayrollAuctionTheme.OTP
                        setSignerStep={setSignerStep}
                        setSignData={setSignData}
                        signData={signData}
                        onSendOTPCode={onSendOTPCode}
                        appTheme={appTheme}
                    />
                )
            }
            return (
                <OTPPage
                    setSignerStep={setSignerStep}
                    setSignData={setSignData}
                    signData={signData}
                    onSendOTPCode={onSendOTPCode}
                    appTheme={appTheme}
                />
            )
        } else if (signerStep === "signed") {
            if (signData.signature_settings?.custom_theme === 'credit_operation_and_social_security_cards') {
                return (
                    <CreditOperationAndSocialSecurityCardsTheme.SignedPage
                        signData={signData}
                    />
                )
            }
            if (signData.signature_settings?.custom_theme === 'social_security_benefit_card') {
                return (
                    <SocialSecurityBenefitCardTheme.SignedPage
                        signData={signData}
                    />
                )
            }
            if (signData.signature_settings?.custom_theme === 'private_payroll_auction') {
                return (
                    <PrivatePayrollAuctionTheme.SignedPage
                        signData={signData}
                    />
                )
            }
            return (
                <SignedPage
                    setSignMetadata={setSignMetadata}
                    alreadySigned={alreadySigned}
                    signerHash={signerHash}
                    signData={signData}
                    setSignData={setSignData}
                    appTheme={appTheme}
                />
            )
        } else {
            return (
                <NotFoundPage />
            )
        }
    }

    return (
        <ErrorBoundaryComponent>
            {getScenes()}
        </ErrorBoundaryComponent>
    )
}